import { Bolt, FileCopy } from '@styled-icons/material';
import { SPACE_ACTION } from '@typedream/data';
import { Text } from '@typedream/ui';
import { useRouter } from 'next/router';
import { useCallback, useContext, useEffect, useState } from 'react';

import { useSpaceAndPageIDFromRouter } from '@/data/space/hooks';
import OptionsContainer from '@/sharedComponents/Options';
import OptionsItem from '@/sharedComponents/Options/OptionsItem';
import { useGetPageQuery, useGetSpaceQuery } from '@/store';

import { SPACE_SUBSCRIPTIONS, SUBSCRIPTION_PLAN_NAME } from '@/constants';
import Popup from '@/sharedComponents/TextEditor/components/Popup';
import { SUBSCRIPTION_PLAN } from '@/types';
import TokenContext from '@/utils/jwt/tokenContext';
import Badge from '../Badge';
import { InfoCard } from '../InfoCard';
import type { PublishPagesType } from './type';

function PublishPages({
  showTitle = false,
  currentPageOnly,
  setcurrentPageOnly,
}: PublishPagesType) {
  const [spaceID, pageID] = useSpaceAndPageIDFromRouter();
  const [isUpgradeOpen, setisUpgradeOpen] = useState(false);
  const { data: page } = useGetPageQuery([spaceID, pageID]);
  const { data: space } = useGetSpaceQuery([spaceID]);
  const { subscriptionTokenHandler } = useContext(TokenContext);
  const [isUpgradeRequired, setisUpgradeRequired] = useState(false);
  const [plan, setplan] = useState(SUBSCRIPTION_PLAN.FREE);
  const router = useRouter();

  // Check Subscription for Publishing
  const checkSubscription = useCallback(async () => {
    const sub = await subscriptionTokenHandler.getSubscription(spaceID as string);
    if (!sub.isAllowed(SPACE_ACTION.PublishUnlimitedPages, SPACE_SUBSCRIPTIONS)) {
      setcurrentPageOnly(true);
      setisUpgradeRequired(true);
    }
    setplan(sub?.getPlan(SPACE_SUBSCRIPTIONS));
  }, [spaceID, subscriptionTokenHandler, setcurrentPageOnly]);

  useEffect(() => {
    if (spaceID && setcurrentPageOnly) {
      checkSubscription();
    }
  }, [spaceID, setcurrentPageOnly, checkSubscription]);

  return (
    <>
      <Popup
        open={isUpgradeOpen}
        setopen={setisUpgradeOpen}
        showContentBackground={false}
        width="auto"
        height="auto"
      >
        <InfoCard
          title="Upgrade to Publish All Pages!"
          subtitle="Publish all pages, add custom domain, see analytics, setup SEO, and more!"
          icon={<FileCopy fontSize="medium" className="with-accent-text-color" />}
          buttonText="Upgrade to Launch Plan"
          buttonIcon={<Bolt fontSize="small" />}
          onClick={() => {
            router.push(`/settings/${space?.slug}/${spaceID}/billing`);
          }}
        />
      </Popup>
      <div className="flex flex-col items-start gap-2 py-2 px-[18px]">
        {showTitle && (
          <Text as="p" className="mb-0.5" size="sm" weight="medium">
            {isUpgradeRequired ? 'Publish Current Page Only' : 'Publish'}
          </Text>
        )}
        <OptionsContainer
          className="flex items-center w-full text-[13px] font-medium leading-5 !py-2.5 !px-3"
          selected={!currentPageOnly}
          onClick={() => setcurrentPageOnly(false)}
          disabled={isUpgradeRequired}
          endBadge={
            isUpgradeRequired && (
              <Badge variant="accent" onClick={() => setisUpgradeOpen(true)}>
                <Bolt size={16} />
              </Badge>
            )
          }
        >
          <OptionsItem title="All Pages" className="with-text-color w-full" />
        </OptionsContainer>
        <OptionsContainer
          className="flex items-center w-full text-[13px] font-medium leading-5 !py-2.5 !px-3"
          selected={currentPageOnly}
          onClick={() => setcurrentPageOnly(true)}
        >
          <OptionsItem
            title={`${page?.name} only`}
            className="with-text-color"
            classNameEnd="is-subtitle text-xs ml-5"
          />
        </OptionsContainer>
        {isUpgradeRequired && (
          <Text size="2xs" variant="secondary">
            {`You're on the ${SUBSCRIPTION_PLAN_NAME[plan]} plan. Publishing ${page?.name} will unpublish your other pages.`}
          </Text>
        )}
      </div>
    </>
  );
}

export default PublishPages;
